import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
	Card, CardContent, CardHeader, CardTitle, CardDescription,
	Badge, Button, useToast, Logo03Png, Logo01Png
} from '@hiring-intelligence/ui';
import { YEARS_OF_EXPERIENCE_LABELS } from '@hiring-intelligence/shared';
import { candidatePortalApi, candidateApi } from '@/services/api';
import {
	Loader2, AlertCircle, Briefcase, MapPin, FileText,
	Upload, CheckCircle, LogOut, ChevronRight
} from 'lucide-react';

function getPortalToken(): string | null {
	return sessionStorage.getItem('portal-token');
}

function getPortalCandidate(): any {
	try {
		const raw = sessionStorage.getItem('portal-candidate');
		return raw ? JSON.parse(raw) : null;
	} catch {
		return null;
	}
}

export default function CandidateDashboardPage() {
	const navigate = useNavigate();
	const { toast } = useToast();
	const portalToken = getPortalToken();
	const candidate = getPortalCandidate();
	const [selectedJob, setSelectedJob] = useState<string | null>(null);

	// Redirect if not authenticated
	if (!portalToken) {
		navigate('/portal/login');
		return null;
	}

	// Fetch jobs 
	const { data: jobsData, isLoading: jobsLoading, error: jobsError } = useQuery({
		queryKey: ['portal-jobs', portalToken],
		queryFn: () => candidatePortalApi.getJobs(portalToken!),
		enabled: !!portalToken
	});

	// Fetch job detail when selected
	const { data: jobDetail, isLoading: detailLoading } = useQuery({
		queryKey: ['portal-job-detail', portalToken, selectedJob],
		queryFn: () => candidatePortalApi.getJobDetail(portalToken!, selectedJob!),
		enabled: !!portalToken && !!selectedJob
	});

	const handleLogout = () => {
		sessionStorage.removeItem('portal-token');
		sessionStorage.removeItem('portal-candidate');
		navigate('/portal/login');
	};

	const jobs = jobsData?.data || [];
	const detail = jobDetail?.data;

	// Upload CV for a specific job via the legacy upload endpoint
	const handleUploadCV = async (file: File, uploadToken: string) => {
		try {
			const formData = new FormData();
			formData.append('token', uploadToken);
			formData.append('cv', file);
			await candidateApi.uploadCV(formData);
			toast({ title: 'Success', description: 'CV uploaded successfully!' });
		} catch (err) {
			toast({ title: 'Error', description: err instanceof Error ? err.message : 'Upload failed', variant: 'destructive' });
		}
	};

	if (jobsLoading) {
		return (
			<div className="min-h-screen bg-gray-50 flex items-center justify-center">
				<Loader2 className="h-8 w-8 animate-spin text-gray-400" />
			</div>
		);
	}

	if (jobsError) {
		return (
			<div className="min-h-screen bg-gray-50 flex items-center justify-center">
				<Card className="max-w-md w-full mx-4">
					<CardContent className="flex flex-col items-center py-12">
						<AlertCircle className="h-12 w-12 text-red-500 mb-4" />
						<h2 className="text-xl font-semibold mb-2">Session Expired</h2>
						<p className="text-muted-foreground text-center mb-4">
							Your session has expired. Please log in again.
						</p>
						<Button onClick={() => navigate('/portal/login')}>Log In</Button>
					</CardContent>
				</Card>
			</div>
		);
	}

	return (
		<div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
			{/* Header */}
			<header className="bg-white border-b">
				<div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between gap-2">
					<div className="flex items-center gap-3">
						<img src={Logo03Png} alt="" className="h-8 w-8" />
						<img src={Logo01Png} alt="Canis Major" className="h-6 hidden sm:block" />
					</div>
					<div className="flex items-center gap-2 sm:gap-3">
						{candidate?.fullName && (
							<span className="text-sm text-gray-600 hidden sm:inline truncate max-w-[150px]">{candidate.fullName}</span>
						)}
						<Button variant="ghost" size="sm" onClick={handleLogout}>
							<LogOut className="h-4 w-4 mr-1" /> Logout
						</Button>
					</div>
				</div>
			</header>

			<div className="max-w-4xl mx-auto px-4 py-8">
				{/* Welcome */}
				<div className="mb-6">
					<h1 className="text-xl sm:text-2xl font-bold text-gray-900">
						{candidate?.fullName ? `Welcome, ${candidate.fullName}` : 'Your Applications'}
					</h1>
					<p className="text-gray-500 mt-1">
						{jobs.length === 0
							? 'No active applications found.'
							: `You have ${jobs.length} active application${jobs.length > 1 ? 's' : ''}.`}
					</p>
				</div>

				{/* Job list */}
				{selectedJob === null ? (
					<div className="space-y-3">
						{jobs.map((job: any) => (
							<Card
								key={job.hiringRequestId}
								className="cursor-pointer hover:shadow-md transition-shadow"
								onClick={() => setSelectedJob(job.hiringRequestId)}
							>
							<CardContent className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4">
								<div className="flex items-center gap-3 min-w-0">
									<div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
										<Briefcase className="h-5 w-5 text-primary" />
									</div>
									<div className="min-w-0">
										<h3 className="font-medium truncate">{job.roleTitle}</h3>
										<div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
												{job.company && <span>{job.company}</span>}
												{job.yearsOfExperience && <Badge variant="secondary" className="text-xs">{YEARS_OF_EXPERIENCE_LABELS[job.yearsOfExperience] || job.yearsOfExperience}</Badge>}
												{job.location && (
													<span className="flex items-center gap-1">
														<MapPin className="h-3 w-3" /> {job.location}
													</span>
												)}
											</div>
										</div>
									</div>
									<div className="flex items-center gap-2 sm:ml-auto shrink-0">
										{job.cvStatus === 'uploaded' ? (
											<Badge variant="success" className="text-xs">
												<CheckCircle className="h-3 w-3 mr-1" /> CV Submitted
											</Badge>
										) : (
											<Badge variant="outline" className="text-xs">
												<Upload className="h-3 w-3 mr-1" /> CV Needed
											</Badge>
										)}
										<ChevronRight className="h-4 w-4 text-muted-foreground" />
									</div>
								</CardContent>
							</Card>
						))}
					</div>
				) : (
					/* Job detail view */
					<div className="space-y-4">
						<Button
							variant="ghost"
							size="sm"
							onClick={() => setSelectedJob(null)}
							className="mb-2"
						>
							&larr; Back to Applications
						</Button>

						{detailLoading ? (
							<div className="flex items-center justify-center py-8">
								<Loader2 className="h-6 w-6 animate-spin text-gray-400" />
							</div>
						) : detail ? (
							<>
								{/* Job info */}
								<Card>
									<CardHeader>
										<CardTitle className="flex items-center gap-2">
											<Briefcase className="h-5 w-5" />
											{detail.roleTitle}
										</CardTitle>
										<CardDescription>
											{detail.company && `${detail.company} · `}
									{detail.yearsOfExperience}
											{detail.location && ` · ${detail.location}`}
										</CardDescription>
									</CardHeader>
									{detail.jobDescription && (
										<CardContent>
											{typeof detail.jobDescription === 'string' ? (
												<div className="prose prose-sm max-w-none dark:prose-invert overflow-x-auto prose-table:w-full prose-table:border-collapse prose-th:border prose-th:border-border prose-th:px-3 prose-th:py-2 prose-th:bg-muted prose-th:text-left prose-td:border prose-td:border-border prose-td:px-3 prose-td:py-2">
													<ReactMarkdown remarkPlugins={[remarkGfm]}>{detail.jobDescription}</ReactMarkdown>
												</div>
											) : (
												<pre className="text-sm bg-gray-50 p-4 rounded-lg overflow-auto">
													{JSON.stringify(detail.jobDescription, null, 2)}
												</pre>
											)}
										</CardContent>
									)}
								</Card>

								{/* CV upload */}
								<Card>
									<CardHeader>
										<CardTitle className="flex items-center gap-2">
											<FileText className="h-5 w-5" />
											Your CV
										</CardTitle>
									</CardHeader>
									<CardContent>
										{detail.cvStatus === 'uploaded' ? (
											<div className="flex items-center gap-3 text-green-700 bg-green-50 p-4 rounded-lg">
												<CheckCircle className="h-6 w-6" />
												<div>
													<p className="font-medium">CV Submitted</p>
													<p className="text-sm text-green-600">
														Your CV has been received. The team will review your application.
													</p>
												</div>
											</div>
										) : detail.uploadToken ? (
											<CVUploadWidget
												uploadToken={detail.uploadToken}
												onUpload={handleUploadCV}
											/>
										) : (
											<p className="text-sm text-muted-foreground">
												CV upload is not available for this position at the moment.
											</p>
										)}
									</CardContent>
								</Card>
							</>
						) : null}
					</div>
				)}
			</div>
		</div>
	);
}

function CVUploadWidget({ uploadToken, onUpload }: { uploadToken: string; onUpload: (file: File, token: string) => Promise<void> }) {
	const [file, setFile] = useState<File | null>(null);
	const [uploading, setUploading] = useState(false);
	const [done, setDone] = useState(false);

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (e.target.files?.[0]) setFile(e.target.files[0]);
	};

	const handleSubmit = async () => {
		if (!file) return;
		setUploading(true);
		try {
			await onUpload(file, uploadToken);
			setDone(true);
		} finally {
			setUploading(false);
		}
	};

	if (done) {
		return (
			<div className="flex items-center gap-3 text-green-700 bg-green-50 p-4 rounded-lg">
				<CheckCircle className="h-6 w-6" />
				<p className="font-medium">CV uploaded successfully!</p>
			</div>
		);
	}

	return (
		<div className="space-y-3">
			<div className="border-2 border-dashed rounded-lg p-6 text-center">
				{file ? (
					<div className="flex flex-col items-center">
						<FileText className="h-8 w-8 text-primary mb-2" />
						<p className="font-medium text-sm">{file.name}</p>
						<p className="text-xs text-muted-foreground">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
						<Button variant="ghost" size="sm" onClick={() => setFile(null)} className="mt-1">
							Remove
						</Button>
					</div>
				) : (
					<label className="cursor-pointer flex flex-col items-center">
						<Upload className="h-8 w-8 text-gray-300 mb-2" />
						<span className="text-sm text-gray-500">Click to select or drag your CV here</span>
						<span className="text-xs text-gray-400 mt-1">PDF, DOC, DOCX (max 10MB)</span>
						<input
							type="file"
							accept=".pdf"
							onChange={handleChange}
							className="hidden"
						/>
					</label>
				)}
			</div>
			{file && (
				<Button onClick={handleSubmit} disabled={uploading} className="w-full">
					{uploading ? (
						<><Loader2 className="h-4 w-4 animate-spin mr-2" /> Uploading...</>
					) : (
						'Upload CV'
					)}
				</Button>
			)}
		</div>
	);
}
